import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import Input from '../../../components/bootstrap/forms/Input';
import Icon from '../../../components/icon/Icon';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import SubHeader, {
	SubHeaderLeft,
	SubHeaderRight,
	SubheaderSeparator,
} from '../../../layout/SubHeader/SubHeader';
import { DropdownMenu, DropdownToggle } from '../../../components/bootstrap/Dropdown';
import Button from '../../../components/bootstrap/Button';
import Popovers from '../../../components/bootstrap/Popovers';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Checks, { ChecksGroup } from '../../../components/bootstrap/forms/Checks';
import Card, {
	CardActions,
	CardBody,
	CardHeader,
	CardLabel,
	CardTitle,
} from '../../../components/bootstrap/Card';
import { api_getAllUsers, api_getUsersByFilter } from '../../../api/user';
import { api_deleteUser } from '../../../api/auth';
import { getFirstLetter } from '../../../helpers/helpers';
import Avatar from '../../../components/Avatar';
import { getImageUrl } from '../../../api';
import AuthContext from '../../../contexts/authContext';

const PAGE_SIZE = 10;
const AllUsers = () => {
	const [users, setUsers] = useState<any[]>([]);
	const [loading, setLoading] = useState(false);
	const [deleteLoadingIndex, setDeleteLoadingIndex] = useState<null | number>(null);
	const [pageNo, setPageNo] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	const navigate = useNavigate();
	const { userData } = useContext(AuthContext) as any;
	const [filter, setFilter] = useState(false);
	const [searchString, setSearchString] = useState('');

	const fetchUsers = async (page: number) => {
		try {
			setLoading(true);
			const res = await api_getAllUsers(userData.token, page, PAGE_SIZE);
	
			if (res.isSuccess) {
				if (!res.data.length) {
					setHasMore(false);
					return;
				}

				if (page === 1) {
					setUsers(res.data);
					setPageNo(2);
				} else {
					setUsers((prevUsers) => [...prevUsers, ...res.data]);
					setPageNo(page + 1);
				}
			}
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};
	const getUserByFilter = async () => {
		try {
			setLoading(true);
			const res = await api_getUsersByFilter(searchString);
			if (res.isSuccess) {
				if (!res.data.length) {
					setHasMore(false);
					return;
				}
				setUsers(res.data);
			}
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const onLoadMore = () => {
		if (hasMore) {
			fetchUsers(pageNo);
		}
	};

	const handleDeleteUser = async (userId: string, index: number) => {
		try {
			setDeleteLoadingIndex(index);
			const res = await api_deleteUser(userData.token, userId);
			if (res.isSuccess) {
				setUsers((prevUsers) => prevUsers.filter((user) => user._id !== userId));
			}
		} catch (error) {
			console.error(error);
		} finally {
			setDeleteLoadingIndex(null);
		}
	};

	useEffect(() => {
		fetchUsers(pageNo);
	}, []);

	return (
		<PageWrapper title='All Users'>
			<div style={{ marginTop: '20px' }}>
				<SubHeader>
					<SubHeaderLeft>
						<label
							className='border-0 bg-transparent cursor-pointer me-0'
							htmlFor='searchInput'>
							<Icon
								icon='Search'
								size='2x'
								color='primary'
								onClick={() => {
									setUsers([]);
									setHasMore(true);
									getUserByFilter();
								}}
							/>
						</label>
						<Input
							value={searchString}
							onChange={(e: any) => setSearchString(e?.target?.value)}
							id='searchInput'
							type='search'
							className='border-0 shadow-none bg-transparent'
							placeholder='Search user...'
						/>
					</SubHeaderLeft>
				</SubHeader>
			</div>
			<Card stretch>
				<CardHeader borderSize={1}>
					<CardLabel icon='Alarm' iconColor='info'>
						<CardTitle>All Users</CardTitle>
					</CardLabel>
					<CardActions>
						<Button
							onClick={() => navigate('/add/users')}
							icon='PersonAdd'
							color='primary'
							isLight>
							New User
						</Button>
					</CardActions>
				</CardHeader>
				<CardBody isScrollable className='table-responsive'>
					<table className='table table-modern'>
						<thead>
							<tr>
								<td style={{ width: 60 }} />
								<th>Name</th>
								<th>Email</th>
								<th>Role</th>
								<th>Gender</th>
								<th>Phone No</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{users.map((item, index) => (
								<tr key={item._id}>
									<td>
										<div className='flex-shrink-0'>
											<div
												className='ratio ratio-1x1 me-3'
												style={{ width: 48 }}>
												<div
													className='ratio ratio-1x1 me-3'
													style={{ width: 50 }}>
													{item.avatar ? (
														<img
															src={getImageUrl(item.avatar)}
															style={{
																width: 50,
																height: 50,
																borderRadius: 50,
																objectFit: 'cover',
															}}
															alt={`${item.name}'s avatar`}
														/>
													) : (
														<div>
															<span className='fw-bold'>
																{item.name
																	? getFirstLetter(item.name)
																	: ''}
															</span>
														</div>
													)}
												</div>
											</div>
										</div>
									</td>
									<td>
										<div className='d-flex'>
											<div className='flex-grow-1'>
												<div className='fs-6 fw-bold'>{item.name}</div>
											</div>
										</div>
									</td>
									<td>{item.email}</td>
									<td>{item.role}</td>
									<td>{item.gender}</td>
									<td>{item.phoneNumber}</td>
									<td>
										<span
											onClick={() => navigate('/edit/user', { state: item })}
											style={{ margin: 10, cursor: 'pointer' }}>
											Edit
										</span>
										<span
											onClick={() => handleDeleteUser(item._id, index)}
											style={{ margin: 10, cursor: 'pointer' }}>
											{deleteLoadingIndex === index ? 'Loading...' : 'Delete'}
										</span>
									</td>
								</tr>
							))}
						</tbody>
					</table>
					{hasMore && (
						<div
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
							}}>
							<button
								style={{
									border: 'none',
									padding: '10px',
									borderRadius: '10px',
									marginTop: '10px',
								}}
								onClick={onLoadMore}>
								Load More...
							</button>
						</div>
					)}
				</CardBody>
			</Card>
		</PageWrapper>
	);
};

export default AllUsers;
